import React, { useState, useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { NeonButton } from './NeonButton';
import { useSound } from '../hooks/useSound';
import { ChatMessage } from '../types/game';
import { useToast } from './Toast';

interface ChatPanelProps {
  socket: Socket | null;
  sessionId: string | null;
  playerId: string | null;
}

const CHAT_HISTORY_LIMIT = 200;
const CHAT_MAX_LENGTH = 1000;

export const ChatPanel: React.FC<ChatPanelProps> = ({ socket, sessionId, playerId }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpponentTyping, setIsOpponentTyping] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isOpenRef = useRef(isOpen);
  const typingTimerRef = useRef<number | null>(null);
  const isTypingRef = useRef(false);
  const { playSound } = useSound();
  const { showToast } = useToast();

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!socket || !sessionId) return;

    const requestHistory = () => socket.emit('get-chat-history', { sessionId });
    requestHistory();

    socket.on('chat-history', (history: ChatMessage[]) => {
      setMessages(history.length > CHAT_HISTORY_LIMIT ? history.slice(-CHAT_HISTORY_LIMIT) : history);
    });

    socket.on('chat-message', (message: ChatMessage) => {
      setMessages(prev => {
        const next = [...prev, message];
        return next.length > CHAT_HISTORY_LIMIT ? next.slice(-CHAT_HISTORY_LIMIT) : next;
      });
      if (message.type === 'message' && message.playerId !== playerId) {
        playSound('message');
        if (!isOpenRef.current) {
          setUnreadCount(prev => Math.min(prev + 1, 99));
        }
      }
    });

    socket.on('opponent-typing', (typing: boolean) => {
      setIsOpponentTyping(typing);
    });

    const onConnect = () => {
      requestHistory();
    };

    const onReconnect = () => {
      requestHistory();
    };

    socket.on('connect', onConnect);
    socket.io.on('reconnect', onReconnect);

    return () => {
      socket.off('chat-history');
      socket.off('chat-message');
      socket.off('opponent-typing');
      socket.off('connect', onConnect);
      socket.io.off('reconnect', onReconnect);
    };
  }, [socket, sessionId, playerId, playSound]);

  useEffect(() => {
    if (!isOpen) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [messages.length, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  const handleTyping = (value: string) => {
    setNewMessage(value);
    if (!socket || !sessionId) return;

    if (value.length > 0 && !isTypingRef.current) {
      isTypingRef.current = true;
      socket.emit('typing-start', { sessionId });
    }

    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);

    if (value.length === 0) {
      isTypingRef.current = false;
      socket.emit('typing-stop', { sessionId });
      return;
    }

    typingTimerRef.current = window.setTimeout(() => {
      isTypingRef.current = false;
      socket.emit('typing-stop', { sessionId });
    }, 2000);
  };

  const handleSend = () => {
    if (!socket || !sessionId || !newMessage.trim()) return;
    if (!socket.connected) {
      showToast('Sin conexión: el mensaje no se envió', 'warning');
      return;
    }

    socket.emit('send-chat-message', {
      sessionId,
      playerId,
      message: newMessage.trim()
    });

    setNewMessage('');

    // Detener indicador de escritura
    if (typingTimerRef.current) window.clearTimeout(typingTimerRef.current);
    if (isTypingRef.current) {
      isTypingRef.current = false;
      socket.emit('typing-stop', { sessionId });
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!sessionId) return null;

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          fixed bottom-3 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:bottom-4 sm:right-4 z-40
          ui-button border-2 transition-all duration-300
          ${isOpen 
            ? 'bg-retro-dark border-neon-pink text-neon-pink hover:bg-neon-pink/20' 
            : 'bg-retro-dark/90 border-neon-cyan text-neon-cyan hover:bg-neon-cyan/20'
          }
        `}
      >
        {isOpen ? '💬 CERRAR CHAT' : '💬 CHAT'}
        {!isOpen && unreadCount > 0 && (
          <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-neon-pink text-black text-[10px] font-retro">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed bottom-0 left-0 right-0 sm:bottom-20 sm:right-4 sm:left-auto z-40 w-full sm:w-80 h-[70dvh] sm:h-96 bg-retro-dark border-2 border-neon-cyan rounded-t-lg sm:rounded-lg flex flex-col overflow-hidden shadow-neon-cyan">
          <div className="bg-neon-cyan/20 px-4 py-2 border-b border-neon-cyan/30 flex items-center justify-between gap-3 shrink-0">
            <h3 className="font-retro text-neon-cyan text-sm">💬 CHAT DE LA PARTIDA</h3>
            <button
              onClick={() => setIsOpen(false)}
              aria-label="Cerrar chat"
              className="font-retro text-neon-pink border border-neon-pink/60 rounded px-2 py-0.5 text-xs hover:bg-neon-pink/20 transition"
            >
              ✕
            </button>
          </div>

          <div
            ref={messagesContainerRef}
            className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2 overscroll-contain touch-pan-y"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {messages.length === 0 ? (
              <p className="font-retro text-gray-500 text-xs text-center">
                Sin mensajes aún...
              </p>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`
                    ${msg.type === 'system' 
                      ? 'bg-neon-yellow/10 border border-neon-yellow/30 rounded px-2 py-1' 
                      : msg.playerId === playerId
                        ? 'bg-neon-green/10 border border-neon-green/30 rounded px-2 py-1 ml-4'
                        : 'bg-neon-pink/10 border border-neon-pink/30 rounded px-2 py-1 mr-4'
                    }
                  `}
                >
                  {msg.type === 'system' ? (
                    <p className="font-retro text-neon-yellow text-xs">
                      {msg.message}
                    </p>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`font-retro text-xs ${
                          msg.playerId === playerId ? 'text-neon-green' : 'text-neon-pink'
                        }`}>
                          {msg.playerName}
                        </span>
                        <span className="font-retro text-gray-500 text-xs">
                          {formatTime(msg.timestamp)}
                        </span>
                      </div>
                      <p className="font-retro text-white text-sm break-words">
                        {msg.message}
                      </p>
                    </>
                  )}
                </div>
              ))
            )}

            {/* Indicador de escritura */}
            {isOpponentTyping && (
              <div className="flex items-center gap-2 px-2 py-1">
                <span className="font-retro text-neon-pink text-xs animate-pulse">
                  ✍ escribiendo...
                </span>
              </div>
            )}
          </div>

          <div className="p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] border-t border-neon-cyan/30 shrink-0">
            <div className="flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => handleTyping(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                maxLength={CHAT_MAX_LENGTH}
                placeholder="Escribe un mensaje..."
                className="
                  flex-1 bg-retro-black border border-neon-cyan/50
                  font-retro text-neon-yellow text-sm px-3 py-2 rounded
                  focus:border-neon-cyan focus:outline-none
                  placeholder-gray-500
                "
              />
              <NeonButton
                onClick={handleSend}
                disabled={!newMessage.trim()}
                color="cyan"
                className="px-3"
              >
                ➤
              </NeonButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatPanel;
